// Push Notification Service for The Cybernetics
import api from './api';

const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

class PushNotificationService {
    constructor() {
        this.swRegistration = null;
        this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    }

    // Initialize service worker and check support
    async init() {
        if (!this.isSupported) {
            console.log('Push notifications not supported');
            return false;
        }

        try {
            // Register service worker
            this.swRegistration = await navigator.serviceWorker.register('/sw.js');
            console.log('Service Worker registered');
            return true;
        } catch (error) {
            console.error('Service Worker registration failed:', error);
            return false;
        }
    }

    // Request notification permission
    async requestPermission() {
        if (!this.isSupported) return 'denied';
        
        const permission = await Notification.requestPermission();
        console.log('Notification permission:', permission);
        return permission;
    }

    // Get VAPID public key from backend
    async getVapidPublicKey() {
        try {
            const response = await api.get('/push/vapid-public-key');
            return response.data.publicKey;
        } catch (error) {
            console.error('Failed to get VAPID key:', error);
            return PUBLIC_VAPID_KEY || null;
        }
    }

    // Convert VAPID key for subscription
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    // Subscribe to push notifications
    async subscribe() {
        if (!this.isSupported || !this.swRegistration) {
            console.log('Push not supported or SW not registered');
            return null;
        }

        const permission = await this.requestPermission();
        if (permission !== 'granted') {
            console.log('Notification permission denied');
            return null;
        }

        try {
            const vapidPublicKey = await this.getVapidPublicKey();
            if (!vapidPublicKey) {
                console.error('VAPID public key not available');
                return null;
            }

            const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

            const subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: applicationServerKey
            });

            console.log('Push subscription:', subscription);

            // Send subscription to backend
            const subscriptionJSON = subscription.toJSON();
            await api.post('/push/subscribe', {
                endpoint: subscriptionJSON.endpoint,
                keys: subscriptionJSON.keys
            });

            console.log('Subscription saved to backend');
            return subscription;
        } catch (error) {
            console.error('Failed to subscribe to push:', error);
            return null;
        }
    }

    // Unsubscribe from push notifications
    async unsubscribe() {
        if (!this.swRegistration) return false;

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            if (subscription) {
                // Notify backend
                const subscriptionJSON = subscription.toJSON();
                await api.delete('/push/unsubscribe', {
                    data: {
                        endpoint: subscriptionJSON.endpoint,
                        keys: subscriptionJSON.keys
                    }
                });

                await subscription.unsubscribe();
                console.log('Unsubscribed from push notifications');
                return true;
            }
        } catch (error) {
            console.error('Failed to unsubscribe:', error);
        }
        return false;
    }

    // Check if user is subscribed
    async isSubscribed() {
        if (!this.swRegistration) return false;

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            return !!subscription;
        } catch (error) {
            console.error('Failed to check subscription:', error);
            return false;
        }
    }

    // Get subscription status from backend
    async getStatus() {
        try {
            const response = await api.get('/push/status');
            return response.data;
        } catch (error) {
            console.error('Failed to get push status:', error);
            return { enabled: false, subscribed: false };
        }
    }
}

// Export singleton instance
const pushService = new PushNotificationService();
export default pushService;
